
import { supabase } from '@/integrations/supabase/client';
import { AuthUser } from '@/types/auth';
import { toast } from '@/components/ui/use-toast';
import { storeUser } from './userStorage';
import * as Sentry from "@sentry/react";

/**
 * Handle auth state change after login/signup
 */
export const handleAuthStateChange = async (session: any): Promise<AuthUser | null> => {
  if (!session || !session.user) {
    console.log("No session or user in handleAuthStateChange");
    Sentry.captureMessage("No session or user in handleAuthStateChange", "warning");
    return null;
  }
  
  console.log("Processing auth state change for user:", session.user.id);
  
  // Add auth state processing breadcrumb
  Sentry.addBreadcrumb({
    category: 'auth',
    message: 'Processing auth state change',
    level: 'info',
    data: { userId: session.user.id }
  });
  
  try {
    // Fetch user profile from profiles table
    console.log("Fetching user profile data");
    let profileData = null;
    let profileError = null;
    
    try {
      const profileResult = await supabase
        .from('profiles')
        .select('age_range, education_level, country, email')
        .eq('id', session.user.id)
        .maybeSingle();
      
      profileData = profileResult.data;
      profileError = profileResult.error;
    } catch (error) {
      console.error('Exception fetching user profile:', error);
      Sentry.captureException(error, {
        tags: { action: 'fetch_profile' },
      });
    }
    
    if (profileError) {
      console.error('Error fetching user profile:', profileError);
      Sentry.captureException(profileError, {
        tags: { action: 'fetch_profile' },
      });
    } else {
      console.log("User profile data:", profileData);
    }
    
    // Fetch user roles
    console.log("Fetching user roles");
    let rolesData = [];
    let rolesError = null;
    
    try {
      const rolesResult = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', session.user.id);
      
      rolesData = rolesResult.data || [];
      rolesError = rolesResult.error;
    } catch (error) {
      console.error('Exception fetching user roles:', error);
      Sentry.captureException(error, {
        tags: { action: 'fetch_roles' },
      });
    }
    
    if (rolesError) {
      console.error('Error fetching user roles:', rolesError);
      Sentry.captureException(rolesError, {
        tags: { action: 'fetch_roles' },
      });
    } else {
      console.log("User roles data:", rolesData);
    }
    
    // Create user object with safe fallbacks
    const user: AuthUser = {
      id: session.user.id,
      email: session.user.email || '',
      ageRange: profileData?.age_range || undefined,
      educationLevel: profileData?.education_level || undefined,
      country: profileData?.country || undefined,
      roles: rolesData?.map(r => r.role) || ['user'],
    };
    
    console.log("Created user object from session:", user);
    
    // Store user in localStorage
    storeUser(user);
    
    // Add session processing successful breadcrumb
    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'Auth session processed successfully',
      level: 'info'
    });
    
    return user;
  } catch (error) {
    console.error("Error in handleAuthStateChange:", error);
    Sentry.captureException(error, {
      tags: { action: 'handle_auth_state_change' },
    });
    
    // Return a default user object to prevent getting stuck
    const defaultUser: AuthUser = {
      id: session.user.id,
      email: session.user.email || '',
      roles: ['user'],
    };
    console.log("Returning default user due to error:", defaultUser);
    storeUser(defaultUser);
    return defaultUser;
  }
};
